/* eslint-disable no-loops/no-loops */

import { PrivateRoute, useAuth } from '@omnipresentgroup/auth';
import { AUTH0_ROLES, IS_E2E } from '@omnipresentgroup/constants';
import {
  Navigation,
  type NavMenuItems,
  type NavSectionProps,
  RootLayout,
} from '@omnipresentgroup/design-system';
import { useCompany, useReportingFlags } from '@omnipresentgroup/domains';
import {
  getUserMainRole,
  hasFinanceScope,
  hasPayrollScope,
  hasSuperManagerScope,
} from '@omnipresentgroup/helpers';
import { useFlags } from '@omnipresentgroup/launchdarkly';
import {
  managerNavItems,
  type NavigateFunction,
} from '@omnipresentgroup/nav-items';
import { UserRoleType } from '@omnipresentgroup/types';
import * as Sentry from '@sentry/react';
import { useQueryErrorResetBoundary } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router';

import { environment } from '../environment';
import { ErrorBoundaryFallback } from './components';
import * as constants from './constants';
import { useLaunchDarkly, useSetupIntercom } from './hooks';
import { WrappedMixpanel } from './mixpanelWrapper';

export const Root = () => {
  const navigate = useNavigate();
  const { user: authUser } = useAuth();
  const location = useLocation();

  const { reset } = useQueryErrorResetBoundary();

  const { enableTimesheetsArbeitszeit, enableNewIntegration, enableBenefits } =
    useFlags();

  const isSuperManager = !!authUser && hasSuperManagerScope(authUser);
  const isFinanceManager = !!authUser && hasFinanceScope(authUser);
  const isPayrollManager = !!authUser && hasPayrollScope(authUser);

  const companyId = useMemo(
    () =>
      authUser?.[AUTH0_ROLES].find(
        (role: { type: string }) => role.type === 'manager',
      )?.tenantId || '',
    [authUser],
  );

  const { data: reportingFlags } = useReportingFlags({
    companyId: companyId,
  });

  useLaunchDarkly(authUser);

  const navigateAdapter: NavigateFunction = (path: string) => {
    navigate(path);
  };

  const omniplatformAppUrl = environment.apps.OMNIPLATFORM;
  const managerAppUrl = environment.apps.MANAGER;

  if (!omniplatformAppUrl || !managerAppUrl) {
    throw new Error('Missing app URLs for OmniPlatform and Manager apps');
  }

  const navItems = managerNavItems({
    permissions: {
      isSuperManager,
      isFinanceManager,
      isPayrollManager,
    },
    featureFlags: {
      enablePayrollReportsTab: reportingFlags?.enablePayrollReportsTab,
      enableTimesheetsArbeitszeit,
      enableNewIntegration,
      enableBenefits,
    },
    navigate: navigateAdapter,
    appUrls: {
      omniplatform: omniplatformAppUrl,
      manager: managerAppUrl,
    },
  });

  const userRole = getUserMainRole(authUser?.[AUTH0_ROLES] || []).type || '';

  useEffect(() => {
    WrappedMixpanel.track('Page Viewed', {
      route: location.pathname,
    });
  }, [location.pathname]);

  const { data: company } = useCompany({
    id: companyId,
    enabled: !!companyId,
  });

  useSetupIntercom({
    enabled: !IS_E2E && userRole === UserRoleType.MANAGER,
    appId: environment.intercomAppId,
  });

  // Redirect to the company profile page in OP if the company is not ready
  useEffect(() => {
    if (companyId && company?.needsManagerReview) {
      window.location.href = `${environment.apps.OMNIPLATFORM}/profile/${companyId}/edit`;
    }
  }, [company?.needsManagerReview, companyId]);

  const findSelectedItemId = (items: NavMenuItems) => {
    for (const item of items) {
      // Handle special case for the 'Home'
      if (item.id === '/' && location.pathname === '/') {
        return '/';
      }

      // TODO: this is still not perfect, for a simple reason that /time
      // is still being picked up in case /timesheets had child routes
      const exactMatch = (item as NavSectionProps).items?.find(
        (subItem) => subItem.id === location.pathname,
      );

      if (exactMatch) {
        return exactMatch.id;
      }

      const startsWithMatch = (item as NavSectionProps).items?.find((subItem) =>
        location.pathname.startsWith(subItem.id),
      );

      if (startsWithMatch) {
        return startsWithMatch.id;
      }

      // Check the main item if no sub-item matches
      if (item.id !== '/' && location.pathname.startsWith(item.id)) {
        return item.id;
      }
    }

    return '';
  };

  const NavigationMenu = () => (
    <Navigation
      footerItems={[
        {
          icon: 'Logout',
          id: constants.LOGOUT_PATH,
          name: 'Logout',
          onClick: () => navigate(constants.LOGOUT_PATH),
        },
      ]}
      mainItems={userRole === 'manager' ? navItems : []}
      selectedItemId={findSelectedItemId(navItems)}
      userProfile={{
        companyName: '',
        onClick: () => null,
        userName: authUser?.name || '',
        userRole,
      }}
    />
  );

  return (
    <RootLayout navigation={<NavigationMenu />}>
      <Sentry.ErrorBoundary
        fallback={(props) => <ErrorBoundaryFallback {...props} />}
        onReset={reset}
        key={location.pathname}
      >
        <Outlet />
      </Sentry.ErrorBoundary>
    </RootLayout>
  );
};

export const App = () => PrivateRoute(Root);
